import React, { useEffect, useRef, useState } from 'react';

/** The width the site is laid out at inside the frame, then scaled to fit. */
const PAGE_WIDTH = 1280;

/**
 * The real public site, scaled down to fit its box: what readers see (the live
 * address) or what the working copy looks like (a preview-token address). A
 * drawing of the theme's tokens cannot show the actual home page — a site
 * whose home is a hero and three columns looked like an article list — so
 * Design shows the site itself. The frame is inert (no clicks, no focus).
 */
export function SitePreviewFrame({
  src,
  height,
  title,
  reloadKey,
  eager = false,
  placeholder,
  children,
}: {
  src: string;
  height: number;
  title: string;
  /** Change it to reload the frame (after a save or a template switch). */
  reloadKey?: string;
  /** Load at once (the Design header), not when scrolled near. */
  eager?: boolean;
  /**
   * Shown until the site has loaded — the theme drawn from its tokens — so the
   * box is never a blank grey square for the seconds a real page takes (QA
   * round 7). Without one, a quiet "Loading your site…".
   */
  placeholder?: React.ReactNode;
  /** Overlays: a badge, an open link. */
  children?: React.ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.3);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setScale(Math.max(0.1, el.clientWidth / PAGE_WIDTH));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const key = reloadKey || src;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const loaded = loadedKey === key;
  return (
    <div ref={box} className="ink-site-frame" style={{ height }} aria-busy={!loaded}>
      {!loaded ? (
        <div className="ink-site-frame-wait" aria-hidden="true">
          {placeholder || <span className="ink-site-frame-loading">Loading your site…</span>}
        </div>
      ) : null}
      <iframe
        key={key}
        src={src}
        title={title}
        tabIndex={-1}
        loading={eager ? 'eager' : 'lazy'}
        sandbox="allow-scripts allow-same-origin"
        onLoad={() => setLoadedKey(key)}
        data-loaded={loaded ? 'true' : undefined}
        style={{
          width: PAGE_WIDTH,
          height: Math.ceil(height / scale),
          transform: `scale(${scale})`,
          transformOrigin: '0 0',
        }}
      />
      {children}
    </div>
  );
}

/**
 * From a minted preview URL (…/preview/<token>[?account=…]) the two addresses
 * Design frames: the working copy (?preview=<token>, read per request by the
 * site, so no cookie is involved) and the live site at the same origin. The
 * live one says ?preview=0, so a preview cookie the owner picked up from
 * Preview ↗ doesn't turn "as readers see it" into the working copy
 * (backlog 6a7534c2).
 */
export function previewAddresses(previewUrl: string): { working: string; live: string } {
  const u = new URL(previewUrl);
  const token = u.pathname.split('/preview/')[1] || '';
  u.pathname = '/';
  u.searchParams.delete('next');
  u.searchParams.set('preview', '0');
  const live = u.toString();
  u.searchParams.set('preview', token);
  return { working: u.toString(), live };
}
